'use client';

import { Button, EmptyState, type EmptyStateProps } from '@lulalife/design-system';
import { RefreshCw01 } from '@lulalife/design-system/icons/line';
import { DSSurface } from './DSSurface';

type PropertyTabStateProps = EmptyStateProps & {
  'data-testid'?: string;
  /** A failed load announces itself; an empty list waits its turn. */
  role?: 'status' | 'alert';
  /** Draws the bordered box that SR and Residents keep around their empty state. */
  boxed?: boolean;
  /** Inside a modal body (Figma 8014:8428, 160 tall) instead of filling a tab. */
  compact?: boolean;
};

/**
 * More's baseline rule: empty and error states are centered both ways. The DS Empty State, in a
 * box that fills what's left of the page.
 */
export const PropertyTabState = ({
  'data-testid': testId,
  role = 'status',
  boxed = false,
  compact = false,
  ...emptyState
}: PropertyTabStateProps) => (
  <DSSurface
    className={`flex w-full items-center justify-center ${
      compact ? 'min-h-[160px]' : 'h-full min-h-[320px] flex-1 px-space-16 py-space-24'
    } ${boxed ? 'rounded-lg border border-border-subtle' : ''}`}
    role={role}
    data-testid={testId}
  >
    {/* The DS caps the text at 248; Figma's run the width of their box (8013:103629, 8013:105541). */}
    <EmptyState {...emptyState} className="max-w-none" />
  </DSSurface>
);

/** Figma 8013:106121: the tab's load failed. Sentence case, with Retry. */
export const PropertyTabError = ({
  onRetry,
  'data-testid': testId,
}: {
  onRetry: () => void;
  'data-testid'?: string;
}) => (
  <PropertyTabState
    type="failed"
    role="alert"
    title="Couldn't load property details"
    description="Something went wrong loading these details. Please try again."
    data-testid={testId}
    action={
      <Button
        type="button"
        variant="secondary"
        leftIcon={<RefreshCw01 aria-hidden="true" />}
        onClick={onRetry}
        data-testid={testId ? `${testId}-retry` : undefined}
      >
        Retry
      </Button>
    }
  />
);
